import React, { useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  IconButton,
  Grid,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Stack,
  Divider,
  Box,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import FolderIcon from "@mui/icons-material/Folder";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import { useLayers } from "../../contexts/LayerContext";
import {
  GeoJsonLayerConfig,
  RasterLayerConfig,
  LegendLayerConfig,
  FulcrumLayerConfig,
} from "../../types";
import { styled } from "@mui/material/styles";

// Define props for LayerItem component
interface LayerItemProps {
  layer: LegendLayerConfig;
}

// New interface for FolderItem props
interface FolderItemProps {
  title: string;
  layers: LegendLayerConfig[];
  isFirst?: boolean;
}

const LayerItem = ({ layer }: LayerItemProps) => {
  const { toggleLayerVisibility, visibleLayers } = useLayers();
  const [isOpen, setIsOpen] = useState(false);

  // Check if layer has multiple icons based on a match expression
  const hasMultipleIcons = () => {
    if (
      layer.type === "geojson" &&
      "style" in layer &&
      layer.style &&
      "iconStyle" in layer.style &&
      Array.isArray(layer.style.iconStyle) &&
      layer.style.iconStyle[0] === "match"
    ) {
      return true;
    }
    return false;
  };

  // Extract icon mappings from a match expression
  const getIconMappings = () => {
    if (!hasMultipleIcons()) return [];

    // Cast to GeoJsonLayerConfig to access style safely after checking in hasMultipleIcons
    const geoJsonLayer = layer as GeoJsonLayerConfig;
    const iconStyle = geoJsonLayer.style.iconStyle as any[];
    const mappings = [];

    if (iconStyle[0] === "match") {
      // Check if this is a modulo match: ["match", ["%", ["get", "id"], 7], ...]
      const isModuloMatch = Array.isArray(iconStyle[1]) && iconStyle[1][0] === "%";
      
      if (isModuloMatch) {
        // Modulo match format: ["match", ["%", ["get", "id"], 7], 0, "icon1", 1, "icon2", ...]
        for (let i = 2; i < iconStyle.length - 1; i += 2) {
          const value = iconStyle[i];
          const icon = iconStyle[i + 1];
          
          // Create user-friendly labels based on icon type
          let friendlyLabel = `Sites ${value + 1}`;
          if (icon.includes('Camp')) friendlyLabel = 'Camp Sites';
          else if (icon.includes('Berries')) friendlyLabel = 'Berries/Food Sites';
          else if (icon.includes('Dugong')) friendlyLabel = 'Marine/Water Sites';
          else if (icon.includes('Stone')) friendlyLabel = 'Stone/Artifact Sites';
          else if (icon.includes('Spear')) friendlyLabel = 'Tool/Weapon Sites';
          else if (icon.includes('Waterhole')) friendlyLabel = 'Water Sources';
          else if (icon.includes('Turtle')) friendlyLabel = 'Sacred/Ceremony Sites';
          
          mappings.push({ value: friendlyLabel, icon });
        }
      } else {
        // Standard match format: ["match", ["get", "property"], "value1", "icon1", ...]
        const property = iconStyle[1][1]; // The property being matched (e.g., "type")

        // Start at index 2 and process pairs until the last item (default)
        for (let i = 2; i < iconStyle.length - 1; i += 2) {
          const value = iconStyle[i];
          const icon = iconStyle[i + 1];

          // Handle arrays of values that map to the same icon
          if (Array.isArray(value)) {
            value.forEach((v) => {
              mappings.push({ value: v, icon });
            });
          } else {
            mappings.push({ value, icon });
          }
        }
      }
    }

    return mappings;
  };

  const getStyleIcon = (
    layer: LegendLayerConfig,
    width: number,
    height: number
  ) => {
    // Handle Fulcrum layers as symbol layers
    if (layer.type === "fulcrum") {
      const iconUrl = layer.defaultIcon; // Use the defaultIcon property
      return (
        <img
          src={iconUrl}
          alt="Fulcrum layer"
          style={{ width: `${width}px`, height: `${height}px` }}
        />
      );
    }

    // For raster layers, return a generic icon
    if (layer.type !== "geojson") {
      return (
        <svg
          width={width}
          height={height}
          viewBox={`0 0 ${width} ${height}`}
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect width={width} height={height} fill="#aaa" opacity={0.5} />
        </svg>
      );
    }

    const viewBox = `0 0 ${width} ${height}`;

    // For GeoJSON based on geometry type
    switch (layer.geometry) {
      case "symbol":
        // Return an SVG rectangle with the layer color or default color
        return (
          <svg
            width={width}
            height={height}
            viewBox={viewBox}
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle
              cx={width / 2}
              cy={height / 2}
              r={width / 3}
              fill={
                layer.style["circle-color"] ||
                layer.style.iconStyle?.color ||
                "#3388ff"
              }
              stroke={
                layer.style["circle-stroke-color"] ||
                layer.style.iconStyle?.color ||
                "#ffffff"
              }
              strokeWidth="2"
            />
          </svg>
        );
      case "line":
        // Simple line for line type
        return (
          <svg
            width={width}
            height={height}
            viewBox={viewBox}
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <line
              x1="4"
              y1={height / 2}
              x2={width - 4}
              y2={height / 2}
              stroke={layer.style["line-color"] || "#3388ff"}
              strokeWidth={layer.style["line-width"] || 3}
              strokeLinecap="round"
            />
          </svg>
        );
      case "fill":
        // Rectangle for fill type
        return (
          <svg
            width={width}
            height={height}
            viewBox={viewBox}
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect
              x="2"
              y="2"
              width={width - 4}
              height={height - 4}
              fill={layer.style["circle-color"] || "#3388ff"}
              fillOpacity={layer.style.opacity || 0.6}
              stroke="#ffffff"
              strokeWidth="1"
            />
          </svg>
        );
      case "circle":
        // Circle for point type
        return (
          <svg
            width={width}
            height={height}
            viewBox={viewBox}
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle
              cx={width / 2}
              cy={height / 2}
              r={layer.style["circle-radius"] || width / 3}
              fill={layer.style["circle-color"] || "#3388ff"}
              fillOpacity={layer.style.opacity || 0.8}
              stroke={layer.style["circle-stroke-color"] || "#ffffff"}
              strokeWidth={layer.style["circle-stroke-width"] || 2}
            />
          </svg>
        );
      default:
        // Default icon for other types
        return (
          <svg
            width={width}
            height={height}
            viewBox={viewBox}
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect
              x="2"
              y="2"
              width={width - 4}
              height={height - 4}
              fill="#aaa"
              stroke="#666"
              strokeWidth="1"
            />
          </svg>
        );
    }
  };

  // Get a top-level icon to display next to the layer name
  const getLayerIcon = () => {
    // Don't show icon for layers with multiple icons
    if (hasMultipleIcons()) {
      return null;
    }

    // For other layers, use the normal style icon
    return getStyleIcon(layer, 24, 24);
  };

  // Determine if the expand button should be shown
  const showExpandButton = hasMultipleIcons();

  // Render the icon legend for layers with multiple icons
  const renderIconLegend = () => {
    if (!hasMultipleIcons()) return null;

    const iconMappings = getIconMappings();
    if (iconMappings.length === 0) return null;

    return (
      <Box sx={{ mt: 1, mb: 1 }}>
        <Stack spacing={0.5}>
          {iconMappings.map((mapping, index) => (
            <Box
              key={index}
              sx={{
                display: "flex",
                alignItems: "center",
                mb: 0.5,
                pl: "40px", // Align with the parent layer's text position (same as icon width)
              }}
            >
              <Box
                sx={{
                  mr: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 20,
                }}
              >
                <img
                  src={mapping.icon}
                  alt={`${mapping.value} icon`}
                  style={{ width: "16px", height: "16px" }}
                />
              </Box>
              <Typography variant="caption" sx={{ fontSize: "0.7rem" }}>
                {mapping.value}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Box>
    );
  };

  return (
    <>
      <Grid container spacing={0} alignItems="center">
        {/* Combined column for either expand icon or layer icon */}
        <Grid
          item
          sx={{ width: 40, display: "flex", justifyContent: "center" }}
        >
          {showExpandButton ? (
            <IconButton size="small" onClick={() => setIsOpen(!isOpen)}>
              {isOpen ? <ExpandMoreIcon /> : <ChevronRightIcon />}
            </IconButton>
          ) : (
            getLayerIcon()
          )}
        </Grid>
        <Grid item xs style={{ overflow: "hidden" }}>
          <Typography
            variant="body2"
            noWrap={false}
            onClick={showExpandButton ? () => setIsOpen(!isOpen) : undefined}
            sx={{
              fontSize: "0.8rem",
              cursor: showExpandButton ? "pointer" : "default",
              wordBreak: "break-all",
            }}
          >
            {layer.id}
          </Typography>
        </Grid>
        <Grid
          item
          sx={{ width: 40, display: "flex", justifyContent: "center" }}
        >
          <IconButton
            onClick={() => toggleLayerVisibility(layer.id)}
            sx={{
              p: 0.25,
              width: 40,
              height: 40,
              display: "flex",
              justifyContent: "center",
            }}
          >
            {visibleLayers.has(layer.id) ? (
              <VisibilityIcon fontSize="small" />
            ) : (
              <VisibilityOffIcon fontSize="small" />
            )}
          </IconButton>
        </Grid>
      </Grid>

      {/* Only show icon legend when expanded */}
      {hasMultipleIcons() ? (
        <Collapse in={isOpen} timeout="auto" unmountOnExit>
          {renderIconLegend()}
        </Collapse>
      ) : null}
    </>
  );
};

// Format folder names to be more user-friendly - minimal version
const getFolderName = (type: string): string => {
  switch (type) {
    case "fulcrum":
      return "Fulcrum";
    case "geojson":
      return "Vector";
    case "raster":
      return "Raster";
    case "raster-dem":
      return "Terrain";
    default:
      return type.charAt(0).toUpperCase() + type.slice(1);
  }
};

// Folder item component with clean minimalist header
const FolderItem: React.FC<FolderItemProps> = ({ title, layers, isFirst = false }) => {
  const { toggleLayerVisibility, visibleLayers } = useLayers();
  const [isOpen, setIsOpen] = useState(true); // Folders start open

  // Check if all layers in this folder are visible
  const allLayersVisible = layers.every((layer) => visibleLayers.has(layer.id));
  const someLayersVisible = layers.some((layer) => visibleLayers.has(layer.id));

  // Function to toggle visibility for all layers in the folder
  const toggleFolderVisibility = () => {
    layers.forEach((layer) => {
      if (allLayersVisible) {
        // If all are visible, hide all
        if (visibleLayers.has(layer.id)) {
          toggleLayerVisibility(layer.id);
        }
      } else {
        // Otherwise, show all
        if (!visibleLayers.has(layer.id)) {
          toggleLayerVisibility(layer.id);
        }
      }
    });
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Box
        sx={{
          borderTop: isFirst ? "none" : "1px solid rgba(0,0,0,0.12)",
          borderBottom: "1px solid rgba(0,0,0,0.12)",
          backgroundColor: "#ffffff",
          py: 0.5,
          mb: 1,
        }}
      >
        <Box
          sx={{
            height: "28px",
            display: "flex",
            alignItems: "center",
            px: 0.5,
          }}
        >
          <IconButton
            size="small"
            onClick={() => setIsOpen(!isOpen)}
            sx={{ p: 0.25 }}
          >
            {isOpen ? (
              <ExpandMoreIcon fontSize="small" />
            ) : (
              <ChevronRightIcon fontSize="small" />
            )}
          </IconButton>

          <Typography
            variant="body2"
            onClick={() => setIsOpen(!isOpen)}
            sx={{
              fontWeight: "bold",
              cursor: "pointer",
              ml: 1,
              fontSize: "0.8rem",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
            }}
          >
            {title}
          </Typography>

          <Box sx={{ flexGrow: 1 }} />

          <IconButton
            onClick={toggleFolderVisibility}
            sx={{
              p: 0.25,
              width: 40,
              height: 40,
              display: "flex",
              justifyContent: "center",
            }}
          >
            {allLayersVisible ? (
              <VisibilityIcon fontSize="small" />
            ) : someLayersVisible ? (
              <VisibilityIcon fontSize="small" sx={{ opacity: 0.5 }} />
            ) : (
              <VisibilityOffIcon fontSize="small" />
            )}
          </IconButton>
        </Box>
      </Box>
      <Collapse in={isOpen} timeout="auto" unmountOnExit>
        <Box sx={{ pl: 0, mt: 1 }}>
          <Stack spacing={2}>
            {layers.map((layer) => (
              <LayerItem key={layer.id} layer={layer} />
            ))}
          </Stack>
        </Box>
      </Collapse>
    </Box>
  );
};

const LayerList = () => {
  const { layers } = useLayers();
  const [isOpen, setIsOpen] = useState(true);

  // Filter layers to only include those of type LegendItem
  const legendLayers = layers
    .filter((layer): layer is LegendLayerConfig => {
      return (
        (layer.type === "geojson" ||
          layer.type === "raster" ||
          layer.type === "fulcrum") &&
        !layer.error
      );
    })
    .reverse(); // reverse the list so that the order is same as drawing order on map

  // Group layers by type
  const groupedLayers: Record<string, LegendLayerConfig[]> =
    legendLayers.reduce((acc, layer) => {
      const type = layer.type;
      if (!acc[type]) {
        acc[type] = [];
      }
      acc[type].push(layer);
      return acc;
    }, {} as Record<string, LegendLayerConfig[]>);

  // Hacky way to reduce the extra padding at the bottom of the MapLayers panel when its collapsed
  const CardContentNoPadding = styled(CardContent)(`
    &:last-child {
      padding-bottom: 6px;
    }
  `);

  // Define the order of layer types with fulcrum first
  const layerTypeOrder = ["fulcrum", "geojson", "raster", "raster-dem"];

  // Sort the layer types according to our defined order
  const sortedLayerTypes = Object.keys(groupedLayers).sort((a, b) => {
    const indexA = layerTypeOrder.indexOf(a);
    const indexB = layerTypeOrder.indexOf(b);
    // If a type isn't in our order array, put it at the end
    const posA = indexA === -1 ? 999 : indexA;
    const posB = indexB === -1 ? 999 : indexB;
    return posA - posB;
  });

  return (
    <Card
      elevation={3}
      sx={{
        position: "absolute",
        top: "25px", // Position below SearchBar (which is at top: 16px)
        left: "16px", // Match SearchBar's left position
        width: "300px", // Match SearchBar's width
        maxHeight: "calc(100vh - 130px)",
        display: "flex",
        flexDirection: "column",
        zIndex: 999, // High z-index but below SearchBar's 1000000
      }}
    >
      <CardContentNoPadding sx={{ flexGrow: 0, padding: "8px !important" }}>
        <Box
          sx={{
            overflowY: "auto",
            maxHeight: "calc(100vh - 190px)",
            // Hide scrollbar by default
            scrollbarWidth: "none", // Firefox
          }}
        >
          <Stack spacing={1} sx={{ pr: 1 }}>
            {/* Render folders by layer type, with fulcrum first */}
            {sortedLayerTypes.map((type, index) => (
              <FolderItem 
                key={type} 
                title={getFolderName(type)} 
                layers={groupedLayers[type]}
                isFirst={index === 0}
              />
            ))}
          </Stack>
        </Box>
      </CardContentNoPadding>
    </Card>
  );
};

export default LayerList;
